import React, { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Copy, Check } from 'lucide-react';
import { buildPersonCvUrl } from '@tabbio/utils/public-url';
import { withCvShareRef } from '@tabbio/utils/analytics/cv-traffic';
import { ApprovalBanner } from './approval-banner';
import { CardShell } from './card-shell';
import type { ToolCardProps } from './types';
import { safeGet } from './types';

export const CVPublishCard: FC<ToolCardProps> = ({
  result,
  toolName,
  stage,
  approvalId,
}) => {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);

  // B10: an agent publish waits for the owner. Say what it does before they
  // approve: anyone with the link sees this CV, as it is now.
  if (stage === 'pending_approval' && approvalId) {
    return (
      <CardShell>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500 }}>
              CV
            </span>
          </div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--text-primary)',
              lineHeight: '22px',
            }}
          >
            {t('chat.approval.publishCvTitle')}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: '20px' }}>
            {t('chat.approval.publishCvBody')}
          </div>
        </div>
        <ApprovalBanner approvalId={approvalId} toolName={toolName} />
      </CardShell>
    );
  }

  const title = safeGet<string>(result, 'title');
  const summary = safeGet<string>(result, 'summary');
  const ownerHandle = safeGet<string>(result, 'username') || safeGet<string>(result, 'handle');
  if (!result || typeof result !== 'object') return null;

  const isPublished = toolName === 'cvPublish';
  const publicUrl = ownerHandle ? buildPersonCvUrl(ownerHandle) : null;
  const publicUrlLabel = publicUrl?.replace(/^https?:\/\//, '') ?? null;

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(withCvShareRef(publicUrl, 'copy'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard write failed silently
    }
  };

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Header: icon + label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500 }}>
            CV
          </span>
        </div>

        {/* Title */}
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--text-primary)',
            lineHeight: '22px',
          }}
        >
          {title || 'Untitled CV'}
        </div>

        {/* Summary */}
        {summary && (
          <div
            style={{
              fontSize: 14,
              color: 'var(--text-secondary)',
              lineHeight: '20px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {summary}
          </div>
        )}

        {/* Published URL row */}
        {isPublished && publicUrlLabel ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              background: 'rgba(0,0,0,0.04)',
              borderRadius: 8,
              border: '1px solid var(--gem-outline-variant)',
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: 13,
                fontFamily: 'monospace',
                color: 'var(--text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {publicUrlLabel}
            </span>
            <button
              onClick={handleCopy}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: copied
                  ? 'var(--text-secondary)'
                  : 'var(--text-tertiary)',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'color 150ms ease',
              }}
              onMouseEnter={(e) => {
                if (!copied) e.currentTarget.style.color = 'var(--text-secondary)';
              }}
              onMouseLeave={(e) => {
                if (!copied) e.currentTarget.style.color = 'var(--text-tertiary)';
              }}
              aria-label={copied ? 'Copied' : 'Copy URL'}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        ) : (
          /* Unpublished badge */
          <div>
            <span
              style={{
                display: 'inline-block',
                fontSize: 11,
                color: 'var(--text-tertiary)',
                background: 'rgba(0,0,0,0.04)',
                borderRadius: 9999,
                padding: '2px 10px',
                lineHeight: '18px',
              }}
            >
              Unpublished
            </span>
          </div>
        )}
      </div>
    </CardShell>
  );
};
